import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  useTheme,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search as SearchIcon } from '@mui/icons-material';
import ServicesHero from '../components/services/ServicesHero';
import ServicePackages from '../components/services/ServicePackages';
import ApplicationFlow from '../components/services/ApplicationFlow';
import ApplicationForm from '../components/services/ApplicationForm';
import ProgramSelector from '../components/programs/ProgramSelector';
import { programService, programDataMapper } from '../services/programService';
import MultiLoginDialog from '../components/common/MultiLoginDialog';
import { getApplicationInitData } from '../services/applicationService';
import PaymentConfirmation from '../components/services/PaymentConfirmation';
import apiClient from '../services/api';
import { useUser } from '../contexts/UserContext';
import authManager from '../utils/auth';

// 新增：材料上传组件
import DocumentUpload from '../components/services/DocumentUpload';

const Services = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const preSelectedProgram = location.state?.selectedProgram;

  const [currentStep, setCurrentStep] = useState(0);
  const [applicationData, setApplicationData] = useState({
    selectedLevel: '',
    selectedProgram: preSelectedProgram || null,
    selectedPackage: {},
    personalInfo: {},
    education: [],
    englishenglishLanguage: {},
    workExperience: [],
    emergencyContact: [],
    referee: [],
    payment: false,
    materials: {},
  });
  const [programs, setPrograms] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(''); // ✅ 新增
  const [loginOpen, setLoginOpen] = useState(false);
  const { isLoggedIn: isAuthenticated } = useUser();
  const [selectedProgramObj, setSelectedProgramObj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false); // 新增：提交状态
  const [forceUpdate, setForceUpdate] = useState(0); // 强制更新状态
  const [searching, setSearching] = useState(false); // 新增：搜索loading状态

  // 实时检查登录状态
  const actualIsAuthenticated = React.useMemo(() => {
    const token = localStorage.getItem('auth_token');
    const hasValidToken = token && authManager.isLoggedIn();
    return hasValidToken;
  }, [isAuthenticated, forceUpdate]);

  const steps = [
    '选择专业',
    '选择服务',
    '填写信息',
    '付款确认',
    '准备材料',
    '提交申请',
    '跟踪进度'
  ];

  const progressData = React.useMemo(() => [
    { en: 'Submitted', cn: '申请审核' },
    { en: 'Assigned', cn: '分配顾问' },
    { en: 'Data Review', cn: '资料审核' },
    { en: 'Submit To School', cn: '递交学校' },
    { en: 'School Review', cn: '学校审核' },
    { en: 'Offer', cn: '取得offer' },
    { en: 'Immigration', cn: '入境' },
    { en: 'Admission', cn: '入学' },
    { en: 'Complete', cn: '结案' }
  ], []);

  // 监听登录状态变化
  useEffect(() => {
    const handleAuthTokenCleared = () => {
      setForceUpdate(prev => prev + 1);
    };

    window.addEventListener('authTokenCleared', handleAuthTokenCleared);
    
    return () => {
      window.removeEventListener('authTokenCleared', handleAuthTokenCleared);
    };
  }, []);

  useEffect(() => {
    if (!searchKeyword) {
      setPrograms([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const delayDebounce = setTimeout(() => {
      const fetchPrograms = async () => {
        try {
          const res = await programService.searchPrograms({
            size: 10,
            keyword: searchKeyword,
            academicDegree: selectedLevel,
            type: 'apply'
          });
          const list = res.data?.data || [];
          const mapped = list.map(programDataMapper.mapProgramFromApi);
          setPrograms(mapped);
        } catch (err) {
          // 获取专业失败，使用默认数据
          setPrograms([]);
        } finally {
          setSearching(false);
        }
      };

      fetchPrograms();
    }, 500);

    return () => {
      clearTimeout(delayDebounce);
      setSearching(false);
    };
  }, [searchKeyword, selectedLevel]);

  useEffect(() => {
    setLoading(true);
    const fetchInitData = async () => {
      try {
        // 强制检查登录状态，确保状态同步
        const token = localStorage.getItem('auth_token');
        const hasValidToken = token && authManager.isLoggedIn();
        
        // 只有在用户已登录时才尝试获取申请数据
        let data = null;
        if (actualIsAuthenticated && hasValidToken) {
          try {
            data = await getApplicationInitData();
          } catch (err) {
            // 获取申请数据失败，使用默认数据
          }
        }
        
        let finalData = data;
        // 如果 data 不存在或为空，则使用空数据
        if (!data || Object.keys(data).length === 0) {
          finalData = {
            selectedLevel: '',
            selectedProgram: preSelectedProgram || null,
            selectedPackage: {},
            personalInfo: {},
            education: [],
            englishenglishLanguage: {},
            workExperience: [],
            emergencyContact: [],
            referee: [],
            payment: false,
            materials: {},
          };
        }
        setApplicationData(prev => {
          const newData = {
            ...prev,
            ...finalData
          };
          return newData;
        });
        setPrograms(finalData.programs || []);
        //步骤 - 始终从第一步开始
        setCurrentStep(0); // 选择专业
        //
      } catch (err) {
        setPrograms([]);
      } finally {
        setLoading(false);
      }
    };
    fetchInitData();
  }, [preSelectedProgram, progressData]); // 移除 actualIsAuthenticated 和 forceUpdate 依赖

  useEffect(() => {
    if (applicationData.selectedLevel) {
      setSelectedLevel(applicationData.selectedLevel);
    }
    // 如果有其它需要同步的 state，也可在此处理
  }, [applicationData.selectedLevel]);

  useEffect(() => {
    if (applicationData.selectedProgram) {
      if (typeof applicationData.selectedProgram === 'object') {
        setSelectedProgramObj(applicationData.selectedProgram);
      } else if (programs.length > 0) {
        const found = programs.find(p => p.id === applicationData.selectedProgram);
        setSelectedProgramObj(found || null);
      }
    }
  }, [applicationData.selectedProgram, programs]);

  const handleProgramSelect = (programId) => {
    // 查找 program 对象
    const programObj = programs.find(p => p.id === programId) || null;
    setApplicationData(prev => ({
      ...prev,
      selectedProgram: programObj || programId
    }));
  };

  const handlePackageSelect = (packageData) => {
    setApplicationData(prev => ({
      ...prev,
      selectedPackage: packageData
    }));
    setCurrentStep(2);
  };

  const handleFormDataUpdate = (data) => {
    setApplicationData(prev => ({
      ...prev,
      ...data // 合并表单字段，保留全局字段
    }));
  };

  const handlePaymentComplete = (paymentData) => {
    setApplicationData(prev => ({
      ...prev,
      paymentInfo: paymentData
    }));
    setCurrentStep(4);
  };

  const handleDocumentUpload = (documents) => {
    // 现在DocumentUpload组件自己管理文件状态，不需要更新applicationData
    // 保留这个函数以防将来需要
  };

  const handleSubmitApplication = async () => {
    setSubmitting(true); // 开始提交
    try {
      // 这里假设 applicationData 就是要提交的数据，如需筛选字段可调整
      const res = await apiClient.post('/api/application/submit', applicationData);
      if (res.success === true) {
        setCurrentStep(6); // 跟踪进度
      } else {
        // 可根据需要处理错误
        alert('提交失败，请稍后重试');
      }
    } catch (error) {
      alert('提交申请失败，请检查网络或稍后重试');
    } finally {
      setSubmitting(false); // 结束提交
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSearchChange = (keyword) => {
    setSearchKeyword(keyword);
  };

  const handleRequireLogin = () => setLoginOpen(true);
  const handleLoginSuccess = () => {
    setLoginOpen(false);
  };

  return (
    <Box>
      <ServicesHero
        isAuthenticated={actualIsAuthenticated}
        onRequireLogin={handleRequireLogin}
        onStartApplication={() => setCurrentStep(0)}
      />

      {/* 查询申请按钮区域 */}
      <Box sx={{ 
        py: 4, 
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 600, 
              mb: 2,
              color: theme.palette.text.primary 
            }}>
              查询申请进度
            </Typography>
            <Typography variant="body1" sx={{ 
              color: theme.palette.text.secondary,
              mb: 3
            }}>
              如果您已经提交了申请，可以随时查询申请进度和状态更新
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/application-query')}
              sx={{
                py: 1.5,
                px: 4,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1.1rem'
              }}
            >
              立即查询
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 8 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h2" sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: theme.palette.text.primary,
              }}>
                申请流程
              </Typography>
              <Typography variant="h6" sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}>
                简单7步，完成您的留学申请
              </Typography>
            </Box>

            <Card sx={{ mb: 6, p: 3 }}>
              <Stepper activeStep={currentStep} alternativeLabel>
                {steps.map((label, index) => (
                  <Step key={label} completed={index < currentStep}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Card>

            <Paper id="application-process" sx={{ p: 4, minHeight: 400 }}>
              {!actualIsAuthenticated ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    请先登录后申请
                  </Typography>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button variant="contained" size="large" onClick={handleRequireLogin}>
                      登录后申请
                    </Button>
                    
                  </Stack>
                </Box>
              ) : (
                <>
                  {currentStep === 0 && (
                    <Box>
                      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                        第一步：选择学位专业
                      </Typography>
                      <ProgramSelector
                        programs={programs}
                        selectedProgramId={applicationData.selectedProgram?.id || applicationData.selectedProgram}
                        selectedLevel={selectedLevel}
                        onProgramSelect={handleProgramSelect}
                        onLevelSelect={setSelectedLevel}
                        onContinue={handleNext}
                        showContinueButton={false}
                        variant="compact"
                        onSearchChange={handleSearchChange}
                        searching={searching}
                      />
                      {applicationData.selectedProgram && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                              if (!actualIsAuthenticated) {
                                handleRequireLogin();
                                return;
                              }
                              handleNext();
                            }}
                          >
                            继续选择服务套餐
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                  {currentStep === 1 && (
                    <Box>
                      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                        第二步：选择服务套餐
                      </Typography>
                      <ServicePackages
                        onPackageSelect={handlePackageSelect}
                        selectedPackage={applicationData.selectedPackage}
                        actualIsAuthenticated={actualIsAuthenticated}
                        onRequireLogin={handleRequireLogin}
                        isPaid={applicationData.payment}
                      />
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                          variant="outlined"
                          onClick={handleBack}
                          disabled={loading}
                        >
                          ← 返回上一步
                        </Button>
                        {applicationData.selectedPackage && (
                          <Button
                            variant="contained"
                            onClick={() => {
                              if (!actualIsAuthenticated) {
                                handleRequireLogin();
                                return;
                              }
                              handleNext();
                            }}
                          >
                            继续填写信息
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}

                  {currentStep === 2 && (
                    <ApplicationForm
                      selectedProgram={selectedProgramObj}
                      selectedLevel={selectedLevel}
                      selectedPackage={applicationData.selectedPackage}
                      onFormDataUpdate={handleFormDataUpdate}
                      onNext={handleNext}
                      onBack={handleBack}
                      formData={applicationData}
                      actualIsAuthenticated={actualIsAuthenticated}
                      onRequireLogin={handleRequireLogin}
                    />
                  )}

                  {currentStep === 3 && (
                    <PaymentConfirmation
                      selectedPackage={applicationData.selectedPackage}
                      onPaymentSuccess={handlePaymentComplete}
                      onBack={handleBack}
                      actualIsAuthenticated={actualIsAuthenticated}
                      onRequireLogin={handleRequireLogin}
                    />
                  )}

                  {currentStep === 4 && (
                    <DocumentUpload
                      selectedProgram={selectedProgramObj}
                      selectedLevel={selectedLevel}
                      onUpload={(files) => handleDocumentUpload(files)}
                      onNext={handleNext}
                      onBack={handleBack}
                    />
                  )}

                  {currentStep === 5 && (
                    <Box>
                      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                        第六步：提交申请
                      </Typography>
                      <Alert severity="success" sx={{ mb: 3 }}>
                        恭喜！您的申请已准备就绪，即将提交到目标院校
                      </Alert>
                      
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                              if (!actualIsAuthenticated) {
                                handleRequireLogin();
                                return;
                              }
                              handleSubmitApplication();
                            }}
                            disabled={submitting}
                            sx={{ minWidth: 180, position: 'relative' }}
                          >
                            {submitting && <CircularProgress size={22} sx={{ position: 'absolute', left: 24, top: '50%', mt: '-11px' }} />}
                            提交申请
                          </Button>
                        </Box>
                      <Button
                          variant="outlined"
                          onClick={handleBack}
                          disabled={loading}
                        >
                          ← 返回上一步
                        </Button>
                    </Box>
                  )}

                  {currentStep === 6 && (
                    <Box>
                      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                        跟踪进度
                      </Typography>
                      <Alert severity="success" sx={{ mb: 3 }}>
                        申请已成功提交！您可以通过我们的平台随时跟踪申请进度
                      </Alert>
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          感谢您选择我们的服务！
                        </Typography>
                        {/* <Typography variant="body1" color="text.secondary">
                          我们会在1-2个工作日内与您联系，开始正式的申请流程
                        </Typography> */}
                      </Box>
                      {/* 进度提示区块 */}
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        {/*
                        <Stepper alternativeLabel activeStep={currentProgressId} orientation="horizontal">
                          {progressData.map((activity, idx) => {
                            let color = 'grey';
                            let isRejected = false;
                            let completed = false;

                            if (idx < currentProgressId) {
                              color = 'green';
                              completed = true;
                            } else if (idx === currentProgressId) {
                              if (currentProgressStatus === 'In Progress') {
                                color = 'orange';
                              } else if (currentProgressStatus === 'Reject') {
                                color = 'red';
                                isRejected = true;
                              }
                            }

                            return (
                              <Step key={activity.en} completed={completed}>
                                <StepLabel
                                  StepIconComponent={isRejected ? (() => <CloseIcon color="error" />) : undefined}
                                  sx={{
                                    color,
                                    '& .MuiStepIcon-root': { color }
                                  }}
                                >
                                  {activity.cn}
                                </StepLabel>
                              </Step>
                            );
                          })}
                        </Stepper>
                        {applicationData.reason  && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {applicationData.reason}
                          </Alert>
                        )}
                        */}
                        {applicationData.reason  && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {applicationData.reason}
                          </Alert>
                        )}
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={() => window.location.href = '/dashboard'}
                          sx={{ mt: 2 }}
                        >
                          查看我的申请
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </>
        )}
      </Container>

      
        <ApplicationFlow
          actualIsAuthenticated={actualIsAuthenticated}
          onRequireLogin={handleRequireLogin}
        />
     

      <MultiLoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </Box>
  );
};

export default Services;
